<template>
  <div id="main-content">
    <SuccessBanner :header="isMembershipCreatedFlow ? 'Payment successful' : $route.meta.title">
      <section class="top-content fr-view" v-html="!isCRM ? content?.paymentSuccessfulInformationText : routeParams.content.paymentSuccessfulInformationText" />
    </SuccessBanner>
    <section class="mid-content fr-view">
      <SectionSeparatorWrapper :separator="false" :hidden="this.ismemberFlowError">
        <h3 id="payment-title">Your payment</h3>
        <div class="payment-box">
          <div id="premium-amount" class="box-row">
            <span class="info-title">Premium amount paid</span>
            <p>{{ !isCRM ? premiumAmountPaid : routeParams.premiumAmountPaid }}</p>
          </div>
          <div id="date-paid" class="box-row">
            <span class="info-title">Date paid</span>
            <p>{{ paymentDate }}</p>
          </div>
          <div id="ref-number" class="box-row">
            <span class="info-title">Reference number</span>
            <p>{{ !isCRM ? referenceNumber : routeParams.referenceNumber }}</p>
          </div>
          <div v-if="!isCRM || isMembershipCreatedFlow" id="next-payment-date" class="box-row">
            <span class="info-title">Next payment date</span>
            <p>{{ isMembershipCreatedFlow ? routeParams.nextPaymentDate : nextPaymentDate }}</p>
          </div>
        </div>
      </SectionSeparatorWrapper>
      <SectionSeparatorWrapper>
        <h3 id="payment-title-margin" :hidden="!this.ismemberFlowError">Your payment</h3>
        <div class="call-out call-to-action box col-6 no-margin-bottom">
          <!-- The heading and text is included as one in CMS, only CRM has a separate heading -->
          <div class="content-container">
            <figure v-if="!isCRM">
              <img :src="getImageUrl(content?.paymentSuccessCalloutIcon)" alt="callout-icon" />
            </figure>
            <div class="text-container">
              <div v-if="isCRM" v-html="routeParams.content.callOutHeading" />
              <div v-html="!isCRM ? content?.callOutText : routeParams.content.callOutText" />
            </div>
          </div>
        </div>
      </SectionSeparatorWrapper>
    </section>

    <section :hidden="!isMembershipCreatedFlow">
      <div class="col-9">
        <p>Register for the Defence Health Member Portal. You can use this anytime, anywhere to:</p>
        <ul>
          <li>View your cover</li>
          <li>View your claim history</li>
          <li>Make a claim, or</li>
          <li>Use our tools and resources—find a provider, get a quote, and more</li>
        </ul>
        <button @click="crmNavigateToExternalPage">Close</button>
      </div>
    </section>

    <div id="related-pages" v-if="!isCRM && content?.relatedPages.some(Boolean)">
      <h3 class="hidden">Related Pages</h3>
      <QuickLinks :linksData="content.relatedPages" />
    </div>
  </div>
</template>

<script>
import { ERROR_404_PAGE_NOT_FOUND, MY_COVER_MAKE_A_PAYMENT } from '@/router/routeNames';
import { SectionSeparatorWrapper, SuccessBanner, QuickLinks } from '@/components';
import { getCurrentDate, reverseFormatFormDate } from '@general/js/utils';
import { isBuildCRM, getImageUrl } from '@/utils';

const isCRM = isBuildCRM();

export default {
  name: 'PaymentSuccessful',

  components: {
    SectionSeparatorWrapper,
    SuccessBanner,
    QuickLinks,
  },

  props: {
    refData: {
      type: Object,
    },
    routeParams: {
      type: Object,
    },
    isFromCRM: {
      type: Boolean,
      default: false,
    },
  },

  beforeMount() {
    if (!this.refData) {
        this.$router.push({ name: ERROR_404_PAGE_NOT_FOUND });
    }
    if (this.isFromCRM) {
      this.isCRM = this.isFromCRM;
    }
  },

  data() {
    return {
      isMembershipCreatedFlow: this.routeParams?.isMembershipCreatedFlow ?? false,
      ismemberFlowError: this.routeParams?.ismemberFlowError ?? false,
      isCRM,
      MY_COVER_MAKE_A_PAYMENT,
      ERROR_404_PAGE_NOT_FOUND,
      ...this.refData,
    };
  },

  computed: {
    paymentDate() {
      return reverseFormatFormDate(getCurrentDate());
    },
  },

  methods: {
    crmNavigateToExternalPage() {
      const entityFormOptions = {};
      entityFormOptions.entityName = 'dhl_membershippolicy';
      entityFormOptions.entityId = this.policyId;
      // Open the form.
      window.parent.xrmValue.Navigation.openForm(entityFormOptions);
    },
    getImageUrl,
  },
};
</script>

<style lang="scss" scoped>
h1 {
  margin-bottom: 25px;
}

.top-content {
  margin-bottom: 2rem;

  & > * {
    margin-bottom: 0;
  }
}

.mid-content {
  @include flex-row;
  margin: 56px 0;
  justify-content: space-between;

  @include mobile {
    flex-direction: column;
    gap: 24px;
    margin: 1rem 0;
  }
  .no-margin-bottom {
    margin-bottom: 0;
  }
}

.payment-box {
  @include desktop {
    min-width: 650px;
  }

  @include tablet {
    min-width: 450px;
  }

  & > * {
    padding: 0 1.5rem 0 0;
    margin-bottom: 0.75rem;

    &:first-child {
      padding-top: 0.75rem;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  .info-title {
    display: block;
    font-weight: $BOLD;
    margin-bottom: 5px;
  }

  .invisible-title {
    opacity: 0.01;
  }

  .box-row {
    @include grid-layout;
    @include grid-template(1fr 1fr);
    margin-bottom: 0.75rem;

    @include mobile {
      grid-template-columns: 1fr 1fr;
    }

    & > * {
      margin-bottom: 0;
    }
  }

  .box-row:last-child {
    padding-bottom: 0.75rem;
    margin-bottom: 0;
  }

  p {
    margin-bottom: 0;
  }
}

h3.hidden {
  opacity: 0.01;
  margin-bottom: 0;
  height: spacer(5);
  overflow: hidden;
}

#related-pages {
  margin-bottom: 2rem;

  @include mobile {
    margin-bottom: 1rem;
  }
}

#payment-title {
  margin-bottom: 0;
}

#payment-title-margin {
  margin-bottom: 1.5rem;
}

:deep(.separator-header) {
  margin-bottom: 0;
}
:deep(ul:not(.radio-group)) {
  li {
    text-indent: -1.5rem;
    padding-left: 1.5rem;
    margin-bottom: 0.5rem;

    &:before {
      content: '';
      display: inline-block;
      margin-right: 0.5rem;
      height: 15px;
      width: 15px;
      background-repeat: no-repeat;
      background-size: contain;
      background-image: url('/assets/public/images/bullet-icon.svg');
    }
  }
}
</style>
